import { computed, onScopeDispose, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { saveAs } from 'file-saver';
import { StoreSetupService } from '@/api/services/store-setup.service';
import { resolveMenuQrCopyFor } from '@/composition/menu-qr/use-menu-qr-copy';
import { useUi } from '@/composition/use-ui';
import { toMenuQrOrderingContext } from '@/domain/menu-qr/menu-qr-design';
import { buildMenuQrTargets } from '@/domain/menu-qr/menu-qr-targets';
import type { IMenuQrSelection } from '@/domain/menu-qr/menu-qr-targets';
import { buildMenuQrUrl } from '@/domain/menu-qr/menu-qr-url';
import {
  storeLinkLabel,
  storeLinkUrl,
} from '@/domain/store-setup/store-identity';
import { AlertType } from '@/interfaces/IAlert';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import { openExternal } from '@/lib/open-external';
import { loadOptionalImage } from '@/lib/print/menu-qr-canvas';
import {
  canvasToPngBlob,
  renderMenuQrPoster,
} from '@/lib/print/menu-qr-poster-renderer';
import { Globals } from '@/modules/Globals';
import { i18n } from '@/plugins/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { createQrBase64 } from '@/utils';
import { copyTextToClipboard } from '@/utils/clipboard.utils';

/** The on-screen QR image width, in pixels (shown at 200 px). */
export const LIVE_QR_IMAGE_WIDTH = 400;
/** How long «Copiar» reads «Copiado». */
export const LIVE_LINK_COPIED_MS = 2000;

const STOREFRONT_ONLY: IMenuQrSelection = {
  includeStorefront: true,
  includeTakeAway: false,
  includeTables: false,
  selectedTableIds: [],
};

export interface IUseStoreLiveLinkOptions {
  /** Renders the on-screen QR (`qrDataUrl`); off where no QR is shown (the dashboard). */
  qr: boolean;
}

export interface IUseStoreLiveLink {
  /** `https://bistri.fi/{slug}` (the short storefront link). */
  url: ComputedRef<string>;
  /** `bistri.fi/{slug}` as the owner reads it. */
  label: ComputedRef<string>;
  /** The storefront QR (`https://bistri.fi/go/{id}?ref=qr`) as a data URL; null until ready, when it failed or with `qr: false`. */
  qrDataUrl: Ref<string | null>;
  downloadingQr: Ref<boolean>;
  /** True for 2 s after a successful copy. */
  copied: Ref<boolean>;
  /** Copies the link; false (with a toast) when the clipboard failed. */
  copy(): Promise<boolean>;
  /** Opens WhatsApp with `text` ready to share. */
  shareWhatsapp(text: string): Promise<void>;
  /** Opens the storefront as a diner sees it. */
  openAsDiner(): Promise<void>;
  /** Saves `qr-{slug}.png` (the storefront poster) and ticks the QR checklist step; false (with a toast) when it failed. */
  downloadQrPoster(): Promise<boolean>;
}

/**
 * The live store's link and QR on «¡Su tienda está en línea!» (plan §4.9),
 * modelled on `use-reservations-public-link.ts` (Capacitor-aware opens).
 */
export function useStoreLiveLink(
  restaurant: () => IRestaurant | null,
  options: IUseStoreLiveLinkOptions = { qr: true }
): IUseStoreLiveLink {
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const qrDataUrl = ref<string | null>(null);
  const downloadingQr = ref(false);
  const copied = ref(false);
  let copiedTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  const t = (key: string) => String(i18n.t(key));

  const slug = computed(() => restaurant()?.stringId ?? '');
  const url = computed(() =>
    slug.value ? storeLinkUrl(slug.value, Globals.shortWebsite) : ''
  );
  const label = computed(() => (slug.value ? storeLinkLabel(slug.value) : ''));

  watch(
    () => (options.qr ? restaurant()?.id ?? null : null),
    async (id) => {
      qrDataUrl.value = null;
      if (!id) return;
      try {
        const dataUrl = await createQrBase64(
          buildMenuQrUrl({ restaurantId: id }),
          LIVE_QR_IMAGE_WIDTH
        );
        if (!disposed && restaurant()?.id === id) qrDataUrl.value = dataUrl;
      } catch (error) {
        console.error(error);
      }
    },
    { immediate: true }
  );

  async function copy(): Promise<boolean> {
    if (!url.value) return false;
    try {
      await copyTextToClipboard(url.value);
    } catch (error) {
      console.error(error);
      if (!disposed)
        ui.showToast(AlertType.Error, t('storeSetup.live.copyFailed'));
      return false;
    }
    if (disposed) return true;
    copied.value = true;
    if (copiedTimer !== null) clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      copiedTimer = null;
      copied.value = false;
    }, LIVE_LINK_COPIED_MS);
    return true;
  }

  function shareWhatsapp(text: string): Promise<void> {
    return openExternal(`https://wa.me/?text=${encodeURIComponent(text)}`);
  }

  async function openAsDiner(): Promise<void> {
    if (slug.value) await openExternal(`${Globals.website}/${slug.value}`);
  }

  /** The export's sequence for one target: copy, target, logo probe, poster, PNG, save. */
  async function downloadQrPoster(): Promise<boolean> {
    const store = restaurant();
    if (!store?.id || downloadingQr.value) return false;
    const restaurantId = store.id;
    const branch = restaurantStore.selectedBranch;
    downloadingQr.value = true;
    try {
      const copy = await resolveMenuQrCopyFor(store, branch);
      const { targets } = buildMenuQrTargets({
        restaurantId,
        // No branch token: the file encodes exactly the QR on screen.
        branchToken: '',
        tables: [],
        selection: STOREFRONT_ONLY,
        mode: 'auto',
        ordering: toMenuQrOrderingContext(store, branch),
        copy,
        names: {
          storefrontCaption: t('qr.targets.storefront'),
          takeAwayCaption: t('qr.targets.takeAway'),
          genericFile: t('qr.fileName.generic'),
          takeAwayFile: t('qr.fileName.takeaway'),
          tablesFolder: t('qr.fileName.tables'),
        },
      });
      const target = targets[0];
      if (!target) throw new Error('The storefront QR target is missing.');

      let logoUrl = store.logo ? `${Globals.uploadsPath}/${store.logo}` : null;
      if (logoUrl && !(await loadOptionalImage(logoUrl))) {
        if (!disposed) ui.showToast(AlertType.Info, t('qr.error.logo'));
        logoUrl = null;
      }

      const canvas = await renderMenuQrPoster({
        size: 'square',
        design: target.design,
        qrUrl: target.url,
        title: target.title,
        logoUrl,
        steps: copy.steps,
      });
      let blob: Blob;
      try {
        blob = await canvasToPngBlob(canvas);
      } finally {
        // Frees the print-resolution canvas now (iOS caps the total canvas area).
        canvas.width = 0;
        canvas.height = 0;
      }
      saveAs(blob, `qr-${store.stringId}.png`);
      void StoreSetupService.markQrDone(restaurantId);
      return true;
    } catch (error) {
      console.error(error);
      if (!disposed)
        ui.showToast(AlertType.Error, t('storeSetup.live.qrError'));
      return false;
    } finally {
      if (!disposed) downloadingQr.value = false;
    }
  }

  onScopeDispose(() => {
    disposed = true;
    if (copiedTimer !== null) clearTimeout(copiedTimer);
    copiedTimer = null;
  });

  return {
    url,
    label,
    qrDataUrl,
    downloadingQr,
    copied,
    copy,
    shareWhatsapp,
    openAsDiner,
    downloadQrPoster,
  };
}
